import { Link } from 'react-router-dom';
import {
  ArrowRight, AlertTriangle, MessageSquare, Zap, Eye,
  ShoppingCart, Dice5, Hotel, Plane, Trophy, UtensilsCrossed,
  HelpCircle, Wrench, Package, SprayCan, Users, Sparkles
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const OperationalExperiencePage = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  const sectorIcons = [ShoppingCart, Dice5, Hotel, Plane, Trophy, UtensilsCrossed];
  const sectorColors = [
    'text-[#FF7F00] bg-[#FF7F00]/5 border-[#FF7F00]/20',
    'text-purple-500 bg-purple-50 border-purple-200',
    'text-[#0077FF] bg-[#0077FF]/5 border-[#0077FF]/20',
    'text-amber-500 bg-amber-50 border-amber-200',
    'text-emerald-500 bg-emerald-50 border-emerald-200',
    'text-rose-500 bg-rose-50 border-rose-200',
  ];

  return (
    <div className="min-h-screen" data-testid="operational-experience-page">

      {/* ═══ HERO ═══ */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-24">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-gradient-to-br from-[#FF7F00]/5 to-[#0077FF]/5 rounded-full blur-3xl" />
        </div>
        <div className="container-max px-6 md:px-12 lg:px-24 relative">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-caption text-[#0077FF] mb-4 block text-sm" data-testid="exp-label">{t('operationalExperience.hero.label')}</span>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-slate-900 leading-tight tracking-tight mb-6" data-testid="exp-title">
              {t('operationalExperience.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto mb-5">
              {t('operationalExperience.hero.subtitle')}
            </p>
            <p className="text-base text-slate-500 max-w-2xl mx-auto leading-relaxed mb-4">
              {t('operationalExperience.hero.supporting')}
            </p>
            <p className="text-base text-orange-700/80 max-w-2xl mx-auto leading-relaxed italic" data-testid="exp-adoption-line">
              {t('operationalExperience.hero.adoptionLine')}
            </p>
            <p className="text-base text-slate-500 max-w-2xl mx-auto leading-relaxed mt-4" data-testid="exp-risk-experience-line">
              This is not just about risk — it's about making every interaction faster, easier, and more responsive within your environment.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ WHY ADOPTION HAPPENS NATURALLY ═══ */}
      <section className="py-16 md:py-20 bg-white" data-testid="adoption-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <span className="text-caption text-[#0077FF] mb-6 block text-sm text-center">{t('operationalExperience.adoption.label')}</span>
            <div className="space-y-5">
              {(t('operationalExperience.adoption.paragraphs') || []).map((p, i) => (
                <p key={i} className={`text-lg leading-relaxed ${i === 3 ? 'text-slate-800 font-medium' : 'text-slate-600'}`}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ THE HIDDEN GAP ═══ */}
      <section className="py-16 md:py-20 bg-slate-50/70" data-testid="hidden-gap-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-caption text-[#0077FF] mb-4 block text-sm">{t('operationalExperience.hiddenGap.label')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('operationalExperience.hiddenGap.title')}</h2>
            </div>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed mb-8">
              <p>{t('operationalExperience.hiddenGap.intro1')}</p>
              <p>{t('operationalExperience.hiddenGap.intro2')}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {(t('operationalExperience.hiddenGap.examples') || []).map((item, i) => {
                const exIcons = [SprayCan, Package, Hotel, Dice5, Trophy, Plane, UtensilsCrossed, Wrench];
                const ExIc = exIcons[i] || AlertTriangle;
                return (
                  <div key={i} className="flex items-start gap-3 rounded-xl p-4 border border-[#FF7F00]/15 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-colors hover:border-[#FF7F00]/30" style={{ background: 'rgba(255,127,0,0.05)' }}>
                    <ExIc className="w-4 h-4 text-[#FF7F00] flex-shrink-0 mt-1" />
                    <p className="text-sm text-slate-700">{item}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-base text-slate-600 mt-8 leading-relaxed">{t('operationalExperience.hiddenGap.closing')}</p>
          </div>
        </div>
      </section>

      {/* ═══ DISTRIBUTED SERVICE LAYER ═══ */}
      <section className="py-16 md:py-20 bg-white" data-testid="experience-layer-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-caption text-[#0077FF] mb-4 block text-sm">{t('operationalExperience.experienceLayer.label')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('operationalExperience.experienceLayer.title')}</h2>
            </div>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed mb-8">
              <p>{t('operationalExperience.experienceLayer.intro1')}</p>
              <p>{t('operationalExperience.experienceLayer.intro2')}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              {(t('operationalExperience.experienceLayer.interactions') || []).map((item, i) => {
                const icons = [HelpCircle, Wrench, Package, SprayCan, Users];
                const Ic = icons[i] || HelpCircle;
                return (
                  <div key={i} className="flex items-center gap-3 rounded-xl p-4 border border-[#FF7F00]/15 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-colors hover:border-[#FF7F00]/30" style={{ background: 'rgba(255,127,0,0.05)' }}>
                    <Ic className="w-4 h-4 text-[#FF7F00] flex-shrink-0" />
                    <p className="text-sm text-slate-700 font-medium">{item}</p>
                  </div>
                );
              })}
            </div>
            {/* Result statements */}
            <div className="max-w-lg mx-auto space-y-2">
              {(t('operationalExperience.experienceLayer.results') || []).map((r, i) => (
                <p key={i} className="text-base text-slate-800 font-medium text-center">{r}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTOR EXAMPLES ═══ */}
      <section className="py-16 md:py-20 bg-slate-50/70" data-testid="sector-examples-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-caption text-[#0077FF] mb-4 block text-sm">{t('operationalExperience.sectors.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('operationalExperience.sectors.title')}</h2>
          </div>

          {/* Illustrated sector rows (first 4) */}
          <div className="max-w-5xl mx-auto space-y-14 mb-14">
            {(t('operationalExperience.sectors.cards') || []).slice(0, 4).map((card, i) => {
              const Ic = sectorIcons[i] || MessageSquare;
              const color = sectorColors[i];
              const images = ['/assets/images/tapelligence-retail-assistance.png', '/assets/images/tapelligence-casino-service.png', '/assets/images/tapelligence-pool-towel-service.png', '/assets/images/tapelligence-airport-assistance.png'];
              const isEven = i % 2 === 0;
              return (
                <div key={i} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-10`} data-testid={`sector-card-${i}`}>
                  <div className="w-full md:w-[340px] flex-shrink-0">
                    <img loading="eager" decoding="sync" src={images[i]} alt={card.title} className="w-full max-w-[340px] mx-auto h-auto rounded-2xl" />
                  </div>
                  <div className={`flex-1 bg-white rounded-2xl p-6 border ${color.split(' ')[2]} shadow-[0_2px_8px_rgba(0,0,0,0.04)]`}>
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${color.split(' ').slice(1, 2).join(' ')}`}>
                      <Ic className={`w-5 h-5 ${color.split(' ')[0]}`} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">{card.title}</h3>
                    <ul className="space-y-2">
                      {(card.examples || []).map((ex, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="text-[#FF7F00]/50 mt-0.5">•</span>
                          <span>{ex}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Remaining sector cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {(t('operationalExperience.sectors.cards') || []).slice(4).map((card, i) => {
              const idx = i + 4;
              const Ic = sectorIcons[idx] || MessageSquare;
              const color = sectorColors[idx] || sectorColors[0];
              return (
                <div key={idx} className={`bg-white rounded-2xl p-6 border ${color.split(' ')[2]} shadow-[0_2px_8px_rgba(0,0,0,0.04)]`} data-testid={`sector-card-${idx}`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${color.split(' ').slice(1, 2).join(' ')}`}>
                    <Ic className={`w-5 h-5 ${color.split(' ')[0]}`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{card.title}</h3>
                  <ul className="space-y-2">
                    {(card.examples || []).map((ex, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="text-[#FF7F00]/50 mt-0.5">•</span>
                        <span>{ex}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ WHY THIS MATTERS ═══ */}
      <section className="py-16 md:py-20 bg-white" data-testid="why-matters-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-caption text-[#0077FF] mb-4 block text-sm">{t('operationalExperience.whyMatters.label')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('operationalExperience.whyMatters.title')}</h2>
            </div>
            <p className="text-lg text-slate-600 leading-relaxed text-center mb-8">{t('operationalExperience.whyMatters.intro')}</p>
            <div className="max-w-lg mx-auto space-y-2 mb-8">
              {(t('operationalExperience.whyMatters.results') || []).map((r, i) => (
                <p key={i} className="text-base text-slate-800 font-medium text-center">{r}</p>
              ))}
            </div>
            <p className="text-base text-slate-600 text-center leading-relaxed">{t('operationalExperience.whyMatters.closing')}</p>
          </div>
        </div>
      </section>

      {/* ═══ METRICS ═══ */}
      <section className="py-10 md:py-12" style={{ background: 'linear-gradient(135deg, #0F1C2E 0%, #162A46 100%)' }} data-testid="exp-metrics-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {(t('operationalExperience.metrics') || []).map((m, i) => {
              const icons = [Zap, Users, Eye, Sparkles];
              const Ic = icons[i];
              return (
                <div key={i} className="text-center" data-testid={`exp-metric-${i}`}>
                  <Ic className="w-5 h-5 text-[#4DB8FF] mx-auto mb-2" />
                  <p className="text-sm font-semibold text-white mb-1">{m.value}</p>
                  <p className="text-[11px] text-[#4DB8FF]">{m.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-16 md:py-20 bg-white" data-testid="exp-cta-section">
        <div className="container-max px-6 md:px-12 lg:px-24 text-center">
          <p className="text-lg text-slate-600 mb-6">{t('operationalExperience.cta.description')}</p>
          <Link
            to={getLocalizedPath('/implementation')}
            className="inline-flex items-center gap-2 bg-[#FF7F00] text-white px-8 py-3 rounded-full hover:bg-[#E67200] transition-colors font-medium"
            data-testid="exp-cta-btn"
          >
            {t('operationalExperience.cta.button')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default OperationalExperiencePage;
